import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import {
  Plus,
  Play,
  FileEdit,
  Filter,
  Search,

  Clock,
  Calendar,
  User,
  MoreHorizontal,
  Check,
  Trash2,
  Eye,
  Download,

  Share2,
  Users,
  Building2,
  ArrowLeft,
  FileText,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../../foundation/altzero_base/base/components/ui/card";
import { Button } from "../../../foundation/altzero_base/base/components/ui/button";
import { Input } from "../../../foundation/altzero_base/base/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../foundation/altzero_base/base/components/ui/select";
import { Badge } from "../../../foundation/altzero_base/base/components/ui/badge";
import { useToast } from "../../../foundation/altzero_base/base/hooks/use-toast";
import { useUser } from "../../../foundation/altzero_base/base/contextapi/UserContext";
import ScopingAILayout from "../components/ScopingAILayout";
import { ResponsiveTable } from "../components/ResponsiveTable";
import { Pagination, usePagination } from "../components/Pagination";
import { usePermissions } from "../../../foundation/altzero_base/base/contextapi/PermissionsContext";

// Import sharing functionality
import { DocumentSharingModal } from "../components/sharing/DocumentSharingModal";

// Import tender functionality
import { getTenders, deleteTender, Tender } from "../services/tenderService";
import { RetailerResponseCell } from "../components/RetailerResponseCell";




interface ScopingDocument {
  id: string;
  title: string;
  status: string;
  client_id?: string;
  created_at: string;
  updated_at: string;
  client_name?: string;
  // Shared document specific fields
  is_shared?: boolean;
  shared_by_name?: string;
  permission_level?: string;
  shared_at?: string;
  expires_at?: string;
  share_source?: "direct" | "team" | "organisation";
  share_source_name?: string;
  entity_type?: string;
}

export default function ScopingProposals() {
  const navigate = useNavigate();
  const { isLoading: userIsLoading } = useUser();
  const { can } = usePermissions();
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [clientFilter, setClientFilter] = useState("all");
  const [tenders, setTenders] = useState<Tender[]>([]);
  const [filteredTenders, setFilteredTenders] = useState<Tender[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const { toast } = useToast();



  // Delete confirmation state
  const [deleteTarget, setDeleteTarget] = useState<Tender | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Sharing state (keeping for compatibility)
  const [isSharingModalOpen, setIsSharingModalOpen] = useState(false);
  const [selectedDocumentForSharing, setSelectedDocumentForSharing] = useState<ScopingDocument | null>(null);

  // Load tenders
  useEffect(() => {
    loadTenders();
  }, []);

  // Filter tenders based on search and filters
  useEffect(() => {
    filterTenders();
  }, [searchQuery, statusFilter, clientFilter, tenders]);

  const loadTenders = async () => {
    setIsLoading(true);
    const result = await getTenders();
    if (result.success && result.data) {
      setTenders(result.data);
    } else {
      toast({
        title: "Error",
        description: result.error || "Failed to load tenders",
        variant: "destructive",
      });
    }
    setIsLoading(false);
  };

  const filterTenders = () => {
    let filtered = tenders;

    // Search filter
    if (searchQuery) {
      filtered = filtered.filter((tender) =>
        tender.opportunity_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        tender.client?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        tender.status.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Status filter
    if (statusFilter !== "all") {
      filtered = filtered.filter((tender) => tender.status === statusFilter);
    }

    // Client filter
    if (clientFilter !== "all") {
      filtered = filtered.filter((tender) =>
        tender.client?.name?.toLowerCase().includes(clientFilter.toLowerCase())
      );
    }

    setFilteredTenders(filtered);
  };

  // Add tender-specific functions
  const handleViewDetails = (tender: Tender) => {
    navigate(`/tender/details/${tender.id}`);
  };



  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    const result = await deleteTender(deleteTarget.id);
    setDeleting(false);
    setDeleteTarget(null);
    if (result.success) {
      toast({ title: "Tender deleted", description: `"${deleteTarget.opportunity_name}" has been deleted.` });
      loadTenders();
    } else {
      toast({ title: "Error", description: result.error || "Failed to delete tender", variant: "destructive" });
    }
  };

  // Get unique clients for filter dropdown
  const uniqueClients = Array.from(
    new Set(
      tenders
        .map(tender => tender.client?.name)
        .filter(name => name && name !== "Unknown Client")
    )
  ).sort();

  // Sort tenders by created_at (newest first)
  const sortedTenders = filteredTenders.sort((a, b) => {
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
  });

  // Add pagination
  const {
    currentPage,
    totalPages,
    paginatedData: paginatedTenders,
    setCurrentPage,
    totalItems,
    itemsPerPage,
  } = usePagination(sortedTenders, 10);

  // Format date for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Get status badge with proper styling for tenders
  const getStatusBadge = (status: string) => {
    const configs: Record<string, { variant: "default" | "secondary" | "destructive" | "outline"; color: string; label: string }> = {
      draft:                       { variant: "secondary",   color: "bg-gray-100 text-gray-700",    label: "Draft" },
      waiting_for_retailers_reply: { variant: "outline",     color: "bg-yellow-100 text-yellow-800", label: "Awaiting Replies" },
      retailers_replied:           { variant: "default",     color: "bg-teal-100 text-teal-800",    label: "Retailers Replied" },
      under_review:                { variant: "outline",     color: "bg-orange-100 text-orange-800", label: "Under Review" },
      tender_ready:                { variant: "default",     color: "bg-indigo-100 text-indigo-800", label: "Tender Ready" },
      sent_to_client:              { variant: "default",     color: "bg-blue-100 text-blue-800",    label: "Send to Client" },
      won:                         { variant: "default",     color: "bg-green-100 text-green-800",  label: "Won" },
      lost:                        { variant: "destructive", color: "bg-red-100 text-red-800",      label: "Loss" },
    };

    const config = configs[status] ?? { variant: "outline" as const, color: "bg-gray-100 text-gray-700", label: status.charAt(0).toUpperCase() + status.slice(1).replace(/_/g, ' ') };

    return (
      <Badge variant={config.variant} className={config.color}>
        {config.label}
      </Badge>
    );
  };





  // Show loading state while checking authentication
  if (userIsLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <ScopingAILayout>
      <div className="w-full py-6 px-4 sm:px-6 lg:px-8">
        <div className="mb-6 sm:mb-8">
          <Link
            to="/tender/resource-library"
            className="inline-flex items-center text-sm text-muted-foreground hover:text-primary mb-4 transition-colors"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Resource Library
          </Link>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 md:gap-4">
              <div className="bg-primary/10 p-2 md:p-3 rounded-lg">
                <FileText className="h-5 w-5 md:h-6 md:w-6 text-primary" />
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold">Tenders</h1>
                <p className="text-sm md:text-base text-muted-foreground">
                  Manage all tenders with client details
                </p>
              </div>
            </div>
            {can('tender.create') && (
              <div>
                <Button
                  onClick={() => navigate('/tender/new-proposal')}
                  className="flex items-center gap-2"
                >
                  <Plus className="h-4 w-4" />
                  New Proposal
                </Button>
              </div>
            )}
          </div>
        </div>
        {/* Tender Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-blue-700">Total Tenders</p>
                  <h3 className="text-3xl font-bold text-blue-900 mt-2">{tenders.length}</h3>
                </div>
                <div className="p-3 bg-blue-200 rounded-full">
                  <FileText className="h-6 w-6 text-blue-700" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-purple-700">Emails Sent</p>
                  <h3 className="text-3xl font-bold text-purple-900 mt-2">
                    {tenders.reduce((sum, t) => sum + (t.retailers_sent_count || 0), 0)}
                  </h3>
                  <p className="text-xs text-purple-600 mt-1">To retailers</p>
                </div>
                <div className="p-3 bg-purple-200 rounded-full">
                  <Users className="h-6 w-6 text-purple-700" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-green-700">Replies Received</p>
                  <h3 className="text-3xl font-bold text-green-900 mt-2">
                    {tenders.reduce((sum, t) => sum + (t.retailers_replied_count || 0), 0)}
                  </h3>
                  <p className="text-xs text-green-600 mt-1">From retailers</p>
                </div>
                <div className="p-3 bg-green-200 rounded-full">
                  <Check className="h-6 w-6 text-green-700" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-amber-700">Active Tenders</p>
                  <h3 className="text-3xl font-bold text-amber-900 mt-2">
                    {tenders.filter(t =>
                      t.status === 'waiting_for_retailers_reply' ||
                      t.status === 'retailers_replied' ||
                      t.status === 'under_review'
                    ).length}
                  </h3>
                  <p className="text-xs text-amber-600 mt-1">In progress</p>
                </div>
                <div className="p-3 bg-amber-200 rounded-full">
                  <Clock className="h-6 w-6 text-amber-700" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
        {/* Search and Filter */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="space-y-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-5 w-5" />
                <Input
                  placeholder="Search tenders by name, client, or status..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 h-12 text-base"
                />
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="w-full sm:w-48">
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger>
                      <div className="flex items-center gap-2">
                        <Filter className="h-4 w-4" />
                        <SelectValue placeholder="Status" />
                      </div>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Statuses</SelectItem>
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="waiting_for_retailers_reply">Awaiting Replies</SelectItem>
                      <SelectItem value="retailers_replied">Retailers Replied</SelectItem>
                      <SelectItem value="under_review">Under Review</SelectItem>
                      <SelectItem value="tender_ready">Tender Ready</SelectItem>
                      <SelectItem value="sent_to_client">Sent to Client</SelectItem>
                      
                      {/* <SelectItem value="submitted">Submitted</SelectItem> */}
                      <SelectItem value="won">Won</SelectItem>
                      <SelectItem value="lost">Lost</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="w-full sm:w-48">
                  <Select value={clientFilter} onValueChange={setClientFilter}>
                    <SelectTrigger>
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4" />
                        <SelectValue placeholder="Client" />
                      </div>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Clients</SelectItem>
                      {uniqueClients.map((client) => (
                        <SelectItem key={client} value={client}>
                          {client}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>



        {/* Tenders Table */}
        <Card>
          <CardHeader>
            <CardTitle>All Tenders ({totalItems})</CardTitle>
            <CardDescription>
              Tenders with client details and comprehensive filtering
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex flex-col items-center justify-center py-12 space-y-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <div className="text-muted-foreground font-medium">Loading tenders...</div>
              </div>
            ) : totalItems === 0 ? (
              <div className="text-center py-12 space-y-4">
                <div className="p-4 bg-gray-100 rounded-full w-16 h-16 mx-auto flex items-center justify-center">
                  <FileText className="w-8 h-8 text-gray-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">No tenders found</h3>
                  <p className="text-muted-foreground max-w-md mx-auto">
                    {searchQuery || statusFilter !== "all" || clientFilter !== "all"
                      ? "Try adjusting your search or filters to find what you're looking for."
                      : "No tenders available in the system."}
                  </p>
                </div>
              </div>
            ) : (
              <ResponsiveTable
                data={paginatedTenders}
                columns={[
                  {
                    key: "opportunity_name",
                    label: "Tender Name",
                    render: (tender) => (
                      <span className="font-medium">{tender.opportunity_name || "—"}</span>
                    ),
                  },
                  {
                    key: "client",
                    label: "Client",
                    render: (tender) => (
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-muted-foreground" />
                        {tender.client?.name || "—"}
                      </div>
                    ),
                  },
                  {
                    key: "status",
                    label: "Status",
                    render: (tender) => getStatusBadge(tender.status),
                  },
                  {
                    key: "retailer_response",
                    label: "Retailer Response",
                    render: (tender) => <RetailerResponseCell tender={tender} />,
                  },
                  {
                    key: "created_at",
                    label: "Created",
                    render: (tender) => formatDate(tender.created_at),
                  },
                ]}
                actions={(tender) => (
                  <>
                    {can('tender.edit') && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          const normalizedStatus = tender.status?.toLowerCase().trim();
                          if (normalizedStatus === 'tender_ready') {
                            navigate(`/tender/details/${tender.id}`);
                          } else {
                            navigate(`/tender/documents/new?tender=${tender.id}`);
                          }
                        }}
                        title="Resume Tender"
                      >
                        <Play className="h-4 w-4 text-green-600" />
                      </Button>
                    )}
                    {can('tender.view') && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleViewDetails(tender)}
                        title="View Details"
                      >
                        <Eye className="h-4 w-4 text-primary" />
                      </Button>
                    )}
                    {can('tender.delete') && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setDeleteTarget(tender)}
                        title="Delete Tender"
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    )}
                  </>
                )}
                mobileCardRender={(tender) => (
                  <div className="space-y-2">
                    <div className="font-medium text-lg">{tender.opportunity_name || "—"}</div>
                    <div className="flex items-center gap-2 text-sm">
                      <Building2 className="h-4 w-4 text-muted-foreground" />
                      {tender.client?.name || "—"}
                    </div>
                    <div className="flex items-center gap-2">
                      {getStatusBadge(tender.status)}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Created: {formatDate(tender.created_at)}
                    </div>
                  </div>
                )}
                emptyMessage="No tenders found."
              />
            )}
            {totalItems > 0 && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                itemsPerPage={itemsPerPage}
                totalItems={totalItems}
              />
            )}
          </CardContent>
        </Card>


      </div>

      {/* Delete Confirmation Modal */}
      {deleteTarget && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => !deleting && setDeleteTarget(null)} />
          <div className="relative z-10 bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
            <div className="flex items-start gap-4 mb-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Delete Tender</h3>
                <p className="mt-1 text-sm text-gray-600">
                  Are you sure you want to delete <span className="font-medium text-gray-900">"{deleteTarget.opportunity_name}"</span>?
                </p>
                <p className="mt-2 text-sm text-red-600">
                  This will permanently delete all associated sites, offers, emails, and documents. This action cannot be undone.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <Button variant="outline" onClick={() => setDeleteTarget(null)} disabled={deleting}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleDeleteConfirm} disabled={deleting}>
                {deleting ? "Deleting..." : "Delete Tender"}
              </Button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Document Sharing Modal */}
      {selectedDocumentForSharing && (
        <DocumentSharingModal
          isOpen={isSharingModalOpen}
          onClose={() => {
            setIsSharingModalOpen(false);
            setSelectedDocumentForSharing(null);
          }}
          documentId={selectedDocumentForSharing.id}
          documentTitle={selectedDocumentForSharing.title}
          canManage={true}
        />
      )}
    </ScopingAILayout>
  );
}







